/**
 * Typed fetchers for the generic connector routes
 * (`/api/connectors/:provider/…`, server `src/modules/connectors/core`).
 * Provider-agnostic by construction — every call takes the provider id from
 * the dashboard's `connections[]` manifest, so a second provider needs no new
 * client code.
 *
 * Follows the api-lib idioms: `apiCall` carries the anon-session header +
 * `X-Client-Timezone` automatically; mutations take a CSRF token and, where a
 * `refreshCsrf` callback is supplied, retry once through
 * `apiCallWithCsrfRefresh` on a stale token.
 */
import { apiCall, apiCallWithCsrfRefresh } from "./api";
import type {
  ConnectorImportSummary,
  ConnectorSyncSummary,
  DashboardProviderConnection
} from "./dashboardApi";

/** SPA destination the OAuth callback returns to (allowlisted server-side). */
export type ConnectorReturnToken = "dashboard" | "my_training";

/**
 * Lifecycle of the connection's background sync as reported by the status
 * endpoint. `idle` = no job in flight (a previous run may have completed —
 * check `lastSyncAt` against your baseline).
 */
export type ConnectorSyncState =
  | "idle"
  | "queued"
  | "active"
  | "completed"
  | "failed"
  | "reconnect_required";

export type ConnectorSyncStatus = {
  state: ConnectorSyncState;
  /**
   * Rows touched by the most recent completed run (created + updated +
   * deleted). Null when the server didn't report one — the UI then shows a
   * generic "Sync complete" rather than guessing.
   */
  changedCount: number | null;
  /** Curated user-safe failure copy when `state === "failed"`. */
  error: string | null;
};

/** `GET /api/connectors/:provider/status` — connection + sync state in one poll. */
export type ConnectorStatusResponse = {
  provider: string;
  connected: boolean;
  status: string | null;
  displayName: string | null;
  lastSyncAt: string | null;
  workoutCount: number | null;
  importSummary: ConnectorImportSummary | null;
  syncSummary: ConnectorSyncSummary | null;
  sync: ConnectorSyncStatus | null;
};

/** `POST /api/connectors/:provider/sync-now`. */
export type ConnectorSyncNowResponse = {
  status: string;
  /** True when an already-running job was reused instead of enqueuing a new one. */
  reusedExistingJob: boolean;
};

const SYNC_STATES: readonly ConnectorSyncState[] = [
  "idle",
  "queued",
  "active",
  "completed",
  "failed",
  "reconnect_required"
];

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function parseSkippedWorkouts(raw: unknown): ConnectorImportSummary["skippedWorkouts"] {
  if (!Array.isArray(raw)) return [];
  const out: NonNullable<ConnectorImportSummary["skippedWorkouts"]> = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const bag = item as Record<string, unknown>;
    const dateYmd = str(bag.dateYmd);
    const reason = bag.reason;
    if (
      !dateYmd ||
      (reason !== "no_planned_duration" &&
        reason !== "removed_from_planner" &&
        reason !== "rest_day" &&
        reason !== "race")
    ) {
      continue;
    }
    out.push({
      title: typeof bag.title === "string" ? bag.title : null,
      dateYmd,
      reason,
      ...(typeof bag.sport === "string" ? { sport: bag.sport } : {})
    });
    if (out.length >= 25) break;
  }
  return out;
}

function parseSyncedWorkouts(raw: unknown): NonNullable<ConnectorSyncSummary["importedWorkouts"]> {
  if (!Array.isArray(raw)) return [];
  const out: NonNullable<ConnectorSyncSummary["importedWorkouts"]> = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const bag = item as Record<string, unknown>;
    const dateYmd = str(bag.dateYmd);
    if (!dateYmd) continue;
    out.push({
      title: typeof bag.title === "string" ? bag.title : null,
      dateYmd
    });
    if (out.length >= 25) break;
  }
  return out;
}

function parseSyncSummary(raw: unknown): ConnectorSyncSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const bag = raw as Record<string, unknown>;
  return {
    created: num(bag.created) ?? 0,
    updated: num(bag.updated) ?? 0,
    deleted: num(bag.deleted) ?? 0,
    skippedWorkouts: parseSkippedWorkouts(bag.skippedWorkouts) ?? [],
    importedWorkouts: parseSyncedWorkouts(bag.importedWorkouts),
    updatedWorkouts: parseSyncedWorkouts(bag.updatedWorkouts)
  };
}

/** Tolerant parse — a partial/older server payload degrades, never throws. */
function parseStatus(provider: string, raw: unknown): ConnectorStatusResponse {
  const bag = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const syncBag =
    bag.sync && typeof bag.sync === "object" ? (bag.sync as Record<string, unknown>) : null;
  const state = SYNC_STATES.includes(syncBag?.state as ConnectorSyncState)
    ? (syncBag!.state as ConnectorSyncState)
    : null;
  const summaryBag =
    bag.importSummary && typeof bag.importSummary === "object"
      ? (bag.importSummary as Record<string, unknown>)
      : null;
  return {
    provider: str(bag.provider) ?? provider,
    connected: bag.connected === true,
    status: str(bag.status),
    displayName: str(bag.displayName),
    lastSyncAt: str(bag.lastSyncAt),
    workoutCount: num(bag.workoutCount),
    importSummary: summaryBag
      ? {
          workoutsImported: num(summaryBag.workoutsImported) ?? 0,
          workoutsSkipped: num(summaryBag.workoutsSkipped) ?? 0,
          eventsCreated: num(summaryBag.eventsCreated) ?? 0,
          skippedWorkouts: parseSkippedWorkouts(summaryBag.skippedWorkouts),
          importedWorkouts: parseSyncedWorkouts(summaryBag.importedWorkouts),
          updatedWorkouts: parseSyncedWorkouts(summaryBag.updatedWorkouts)
        }
      : null,
    syncSummary: parseSyncSummary(bag.syncSummary),
    sync: state
      ? { state, changedCount: num(syncBag?.changedCount), error: str(syncBag?.error) }
      : null
  };
}

/**
 * Start OAuth the JSON-first way: fetch `{ authorizeUrl }` (so a missing-shop /
 * server error stays INSIDE the modal instead of a raw JSON error page), then
 * the caller does `window.location.assign(authorizeUrl)`.
 *
 * `?format=json` is the explicit opt-in the server keys on (a default `fetch()`
 * sends a wildcard `Accept` that also matches html, so an `Accept`-based
 * heuristic would misfire). `shop` is passed through so direct-Vite/anon
 * sessions (no proxy shop context) still resolve a storefront; `returnTo`
 * (allowlisted) picks where the callback lands.
 */
export async function startConnectorAuth(args: {
  provider: string;
  shop?: string | null;
  returnTo?: ConnectorReturnToken;
  signal?: AbortSignal;
}): Promise<{ authorizeUrl: string }> {
  const raw = await apiCall<unknown>(
    `/api/connectors/${encodeURIComponent(args.provider)}/auth/start`,
    {
      query: {
        format: "json",
        ...(args.shop ? { shop: args.shop } : {}),
        ...(args.returnTo ? { returnTo: args.returnTo } : {})
      },
      signal: args.signal
    }
  );
  const bag = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const authorizeUrl = str(bag.authorizeUrl);
  if (!authorizeUrl) throw new Error("Couldn't start the connection. Please try again.");
  return { authorizeUrl };
}

/**
 * `GET /api/connectors` — the lightweight provider manifest (`connections[]`),
 * same shape the dashboard serves but without the full aggregate. Powers the
 * Import Data modal's fast path. Degrades to `[]` on a partial/absent payload.
 */
export async function getConnectorsManifest(
  signal?: AbortSignal
): Promise<DashboardProviderConnection[]> {
  const raw = await apiCall<unknown>("/api/connectors", { signal });
  const bag = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return Array.isArray(bag.connections)
    ? (bag.connections as DashboardProviderConnection[])
    : [];
}

export async function getConnectorStatus(
  provider: string,
  signal?: AbortSignal
): Promise<ConnectorStatusResponse> {
  const raw = await apiCall<unknown>(
    `/api/connectors/${encodeURIComponent(provider)}/status`,
    { signal }
  );
  return parseStatus(provider, raw);
}

export async function startConnectorSync(
  provider: string,
  csrfToken: string,
  refreshCsrf?: () => Promise<string>
): Promise<ConnectorSyncNowResponse> {
  return startConnectorJob(provider, "sync-now", csrfToken, refreshCsrf);
}

export async function startConnectorReset(
  provider: string,
  csrfToken: string,
  refreshCsrf?: () => Promise<string>
): Promise<ConnectorSyncNowResponse> {
  return startConnectorJob(provider, "reset", csrfToken, refreshCsrf);
}

async function startConnectorJob(
  provider: string,
  action: "sync-now" | "reset",
  csrfToken: string,
  refreshCsrf?: () => Promise<string>
): Promise<ConnectorSyncNowResponse> {
  const opts = { method: "POST" as const, csrfToken };
  const path = `/api/connectors/${encodeURIComponent(provider)}/${action}`;
  const raw = refreshCsrf
    ? await apiCallWithCsrfRefresh<unknown>(path, opts, refreshCsrf)
    : await apiCall<unknown>(path, opts);
  const bag = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    status: str(bag.status) ?? "queued",
    reusedExistingJob: bag.reusedExistingJob === true
  };
}

/** Disconnect: always clears stored tokens and purges imported workouts. */
export async function disconnectConnector(
  provider: string,
  csrfToken: string,
  refreshCsrf?: () => Promise<string>
): Promise<void> {
  const opts = { method: "POST" as const, csrfToken };
  const path = `/api/connectors/${encodeURIComponent(provider)}/disconnect`;
  if (refreshCsrf) {
    await apiCallWithCsrfRefresh<unknown>(path, opts, refreshCsrf);
  } else {
    await apiCall<unknown>(path, opts);
  }
}

/** Dismiss the system-disconnect reconnect nag (tokens stay; no reconnect). */
export async function acknowledgeConnectorDisconnect(
  provider: string,
  csrfToken: string,
  refreshCsrf?: () => Promise<string>
): Promise<void> {
  const opts = { method: "POST" as const, csrfToken };
  const path = `/api/connectors/${encodeURIComponent(provider)}/acknowledge-disconnect`;
  if (refreshCsrf) {
    await apiCallWithCsrfRefresh<unknown>(path, opts, refreshCsrf);
  } else {
    await apiCall<unknown>(path, opts);
  }
}
